import React, { useEffect, useState } from "react";
import { Icon } from "./Icon";

// Dismissal is per browser tab session and per dead-token episode: the banner
// comes back on the next visit, and the flag is cleared as soon as the
// profile reports needsReauth === false, so a later token loss in the same tab
// shows the banner again. A dead token silently breaks EventSub alerts.
export const REAUTH_DISMISS_KEY = "hal_reauth_banner_dismissed";

const readDismissed = (): boolean => {
  try {
    return window.sessionStorage.getItem(REAUTH_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

const clearDismissed = () => {
  try {
    window.sessionStorage.removeItem(REAUTH_DISMISS_KEY);
  } catch {
    // Storage blocked: nothing to clear.
  }
};

const writeDismissed = () => {
  try {
    window.sessionStorage.setItem(REAUTH_DISMISS_KEY, "1");
  } catch {
    // Storage blocked (private mode etc.) — dismissal just won't persist.
  }
};

interface ReauthBannerProps {
  needsReauth?: boolean;
  // Overridable for tests; defaults to a full-page nav into the Twitch OAuth
  // login flow, which clears users.needsReauth once fresh tokens arrive.
  onReconnect?: () => void;
}

export const ReauthBanner: React.FC<ReauthBannerProps> = ({
  needsReauth,
  onReconnect = () => {
    window.location.href = "/api/auth/twitch";
  },
}) => {
  const [dismissed, setDismissed] = useState(readDismissed);

  // Only an explicit false clears it: undefined means the profile hasn't
  // loaded yet, and clearing then would undo every dismissal on page load.
  useEffect(() => {
    if (needsReauth === false) {
      clearDismissed();
      setDismissed(false);
    }
  }, [needsReauth]);

  if (!needsReauth || dismissed) return null;

  return (
    <div
      role="alert"
      style={{
        maxWidth: "900px",
        margin: "0 auto 16px",
        padding: "14px 18px",
        background: "rgba(239, 68, 68, 0.12)",
        border: "1px solid #EF4444",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
      }}
    >
      <Icon name="alert-triangle" size={18} color="#EF4444" />
      <span
        style={{
          color: "#e2e8f0",
          fontSize: "14px",
          flex: 1,
          minWidth: "200px",
        }}
      >
        Your Twitch connection expired, so HAL can&apos;t read your stream
        events (follows, subs, raids). Reconnect to restore it.
      </span>
      <button
        onClick={onReconnect}
        style={{
          background: "linear-gradient(135deg, #9146FF, #00D4FF)",
          color: "#fff",
          border: "none",
          padding: "10px 20px",
          borderRadius: "8px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Reconnect Twitch
      </button>
      <button
        aria-label="Dismiss"
        title="Dismiss for now"
        onClick={() => {
          writeDismissed();
          setDismissed(true);
        }}
        style={{
          background: "transparent",
          border: "none",
          color: "#94a3b8",
          cursor: "pointer",
          padding: "4px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Icon name="x" size={18} />
      </button>
    </div>
  );
};

export default ReauthBanner;
